import { FelaCSS, colors, transition } from '@bridebook/ui';

interface IStyles {
  wrapper: FelaCSS;
  pagingWrap: FelaCSS;
  bullets: {
    wrap: FelaCSS;
  };
  bullet: (index: number) => FelaCSS;
}

// Width of the dot
const BULLET_WIDTH = 8;
// Spacing between the dots
const BULLET_SPACING = 4;
// Number of dots visible at each side of the active dot (when in the center)
const SIDE_BULLETS = 1;

const styles = (currentIndex: number, imagesCount: number): IStyles => {
  // ########### AUTO CALCULATED ###########
  // First and last slides are different, because the active dot
  // is not in the center but at the side.
  const isFirstSlides = currentIndex < SIDE_BULLETS;
  const isLastSlides = currentIndex > imagesCount - SIDE_BULLETS - 1;
  const fullBulletWidth = BULLET_WIDTH + BULLET_SPACING;
  const maxBullets = SIDE_BULLETS * 2 + 1;
  // Visible number of bullets can be lower than max if there's not enough photos
  const visibleBullets = imagesCount < maxBullets ? imagesCount : maxBullets;
  const sideBullets = imagesCount < maxBullets ? (imagesCount - 1) / 2 : SIDE_BULLETS;
  // #######################################

  const getTranslateX = () => {
    const defaultTranslate = -1 * currentIndex * fullBulletWidth;

    if (isFirstSlides) {
      return defaultTranslate - fullBulletWidth * (sideBullets - currentIndex);
    }

    if (isLastSlides) {
      return defaultTranslate + fullBulletWidth * (sideBullets - imagesCount + currentIndex + 1);
    }

    return defaultTranslate;
  };

  const getOpacity = (index: number) => {
    switch (true) {
      // Current slide
      case index === currentIndex:
        return 1;
      // Regular left siblings
      case currentIndex - sideBullets <= index && index < currentIndex:
      // Regular right siblings
      case currentIndex < index && index <= currentIndex + sideBullets:
      // Additional right-hand side dots for the first slides
      case isFirstSlides && index < visibleBullets:
      // Additional left-hand side dots for the last slides
      case isLastSlides && index > imagesCount - visibleBullets - 1:
        return 0.5;
      // Slides further to the sides are invisible
      default:
        return 0;
    }
  };

  const wrapperWidth = visibleBullets * fullBulletWidth + 10;

  const wrapperHeight = `${BULLET_WIDTH + 2 * BULLET_SPACING}px`;
  const wrapperBorderRadius = 16; //px

  return {
    wrapper: {
      position: 'absolute',
      left: `calc(50% - ${wrapperWidth / 2}px)`,
      bottom: 16,
      width: `${wrapperWidth}px`,
      overflow: 'hidden',
      height: wrapperHeight,
      borderRadius: wrapperBorderRadius,
    },

    pagingWrap: {
      height: '30px',
      position: 'absolute',
      zIndex: 3,
      right: '0',
      left: '0',
      justifyContent: 'center',
      flexDirection: 'row',
      transition: `all ${transition.medium} ease-in-out`,
      transform: `translateX(${getTranslateX()}px)`,
    },
    bullets: {
      wrap: {
        position: 'absolute',
        width: `${imagesCount * fullBulletWidth + 2 * BULLET_SPACING}px`,
        height: wrapperHeight,
        transform: `translateX(${((imagesCount - 1) * fullBulletWidth) / 2}px)`,
        borderRadius: wrapperBorderRadius,
        background: 'rgba(26, 44, 64, 0.5)',
      },
    },
    bullet: (index: number) => ({
      width: `${BULLET_WIDTH}px`,
      height: `${BULLET_WIDTH}px`,
      borderRadius: '100%',
      backgroundColor: colors.white,
      opacity: getOpacity(index),
      transition: `all ${transition.medium} ease-in-out`,
      position: 'absolute',
      left: `calc(50% - ${BULLET_WIDTH / 2})`,
      top: 4,
      transform: `translateX(${index * fullBulletWidth}px)`,
    }),
  };
};

export default styles;
